import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { type VisibleViewPort } from "shared/context/VisibleViewPortContext";
import { parseViewportSize } from "shared/helpers/device-info/getScreenInfo";
import { ENV } from "hub/src/appConstants/env";
import { setParentViewport } from "hub/src/utils/iframe/parentViewport";
import { isIframeSSOPage } from "hub/src/utils/isIframeSSOPage";

/**
 * Manages iframe page sizing when the hub is embedded in the SSO iframe:
 *
 *  - Posts the full content height to the parent (SET_IFRAME_HEIGHT) so the
 *    parent can stretch the iframe, and forwards wheel events (FORWARD_SCROLL)
 *    so the parent page does the scrolling.
 *  - Because the iframe is stretched to its full height and never scrolls
 *    itself, a `position: fixed` overlay would anchor to the whole (tall)
 *    document. So we also track the slice of the iframe currently visible in
 *    the parent's viewport (SET_VISIBLE_VIEWPORT) and return it, letting
 *    overlays (modals) render where the user is actually looking.
 *
 * Returns the visible viewport, or null when not embedded.
 */
export const useIFramePageSize = (): VisibleViewPort | null => {
  const [visibleViewPort, setVisibleViewPort] =
    useState<VisibleViewPort | null>(null);
  const location = useLocation();

  const isEmbedded =
    isIframeSSOPage &&
    !!ENV.REACT_APP_IFRAME_SSO_REDIRECT_URL &&
    typeof ENV.REACT_APP_IFRAME_SSO_REDIRECT_URL === "string";

  const parentOrigin = isEmbedded
    ? ENV.REACT_APP_IFRAME_SSO_REDIRECT_URL!.match(/https:\/\/[^/]+/)![0]
    : null;

  useEffect(() => {
    if (!parentOrigin) {
      return;
    }

    // Disable ion-content internal scrolling so the parent page handles all scrolling.
    // Without this, ion-content creates a second scrollbar inside the iframe.
    const style = document.createElement("style");
    style.textContent = "ion-content { --overflow: hidden; }";
    document.head.appendChild(style);

    let lastPostedHeight = 0;

    const postHeight = () => {
      // Measure the ion-header height (toolbar area) + content container scrollHeight.
      // We must include the header because ion-content sits below it, so the full
      // iframe height needs to account for both.
      const header = document.querySelector("ion-header");
      const headerHeight = header ? header.offsetHeight : 0;

      const contentEl = document.querySelector(
        ".page-content-container",
      ) as HTMLElement | null;
      const contentHeight = contentEl
        ? contentEl.scrollHeight
        : Math.max(
            document.body.scrollHeight,
            document.documentElement.scrollHeight,
          );

      const height = headerHeight + contentHeight;

      // Only post if height changed by more than 10px to avoid feedback loops
      if (Math.abs(height - lastPostedHeight) < 10) {
        return;
      }
      lastPostedHeight = height;

      window.parent.postMessage(
        { type: "SET_IFRAME_HEIGHT", source: "pillar", height },
        parentOrigin,
      );
    };

    // Forward wheel events to parent so the page scrolls when mouse is over iframe.
    // With ion-content scrolling disabled, the iframe has no internal scroll, so
    // wheel events need to reach the parent page.
    const onWheel = (e: WheelEvent) => {
      window.parent.postMessage(
        { type: "FORWARD_SCROLL", source: "pillar", deltaY: e.deltaY },
        parentOrigin,
      );
    };
    window.addEventListener("wheel", onWheel, { passive: true });

    // Track which slice of the iframe is currently visible to the user. The
    // parent posts this on scroll/resize and on request.
    const handleVisibleViewport = (event: MessageEvent) => {
      if (event.origin !== parentOrigin) {
        return;
      }
      if (event.data && event.data.type === "SET_VISIBLE_VIEWPORT") {
        // The parent window's size, sent by newer copies of iframe.html. Used
        // for the screen height and orientation (see `getScreenInfo`); older
        // wrappers leave it out and those constraints are skipped.
        const parentViewport = parseViewportSize({
          width: event.data.viewportWidth,
          height: event.data.viewportHeight,
        });
        if (parentViewport) {
          setParentViewport(parentViewport);
        }

        const top = Number(event.data.visibleTop);
        const height = Number(event.data.visibleHeight);
        if (isNaN(top) || isNaN(height) || height <= 0) {
          return;
        }
        // Skip sub-pixel changes to avoid re-rendering consumers on every
        // scroll frame.
        setVisibleViewPort((prev) =>
          prev &&
          Math.abs(prev.top - top) < 1 &&
          Math.abs(prev.height - height) < 1
            ? prev
            : { top, height },
        );
      }
    };
    window.addEventListener("message", handleVisibleViewport);

    // Poll for height changes — avoids the infinite loop that
    // MutationObserver/ResizeObserver cause when the parent resizes the iframe
    const interval = setInterval(postHeight, 300);

    // Post initial height + request the current visible slice after a short
    // delay so the page has rendered.
    setTimeout(postHeight, 100);
    window.parent.postMessage(
      { type: "REQUEST_VISIBLE_VIEWPORT", source: "pillar" },
      parentOrigin,
    );

    return () => {
      clearInterval(interval);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("message", handleVisibleViewport);
      style.remove();
    };
  }, [parentOrigin]);

  // On client-side navigation (router.push) the parent page keeps its scroll
  // position. Because the iframe is stretched to its full content height and
  // the parent does the scrolling, navigating away while scrolled to the bottom
  // of a long page leaves the new (often shorter) page scrolled past its top.
  // Ask the parent to scroll the iframe back to its top on every route change.
  const isInitialRoute = useRef(true);
  useEffect(() => {
    if (!parentOrigin) {
      return;
    }
    // Skip the initial mount so we don't fight the parent's own initial scroll.
    if (isInitialRoute.current) {
      isInitialRoute.current = false;
      return;
    }
    window.parent.postMessage(
      { type: "SCROLL_TO_IFRAME_TOP", source: "pillar" },
      parentOrigin,
    );
  }, [location.pathname, parentOrigin]);

  return visibleViewPort;
};
